import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import LogoLoader from "../components/common/LogoLoader";
import { API_BASE_URL } from "../config/apiBaseUrl";

const IconMail = () => (
  <svg className="w-8 h-8 text-brandNavy" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const IconCheck = () => (
  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const IconAlert = () => (
  <svg className="w-10 h-10 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const VerifyEmail: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification link. Please use the link from your email.");
      return;
    }

    const run = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          setStatus("error");
          setMessage(data?.message || "Verification failed.");
          return;
        }
        setStatus("ok");
        setMessage(data?.message || "Your email has been verified.");
      } catch {
        setStatus("error");
        setMessage("Network error. Please try again.");
      }
    };

    run();
  }, [token]);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-5 overflow-hidden bg-[#FFF6E9] pb-16">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#8DC7CD]/35 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 h-[28rem] w-[28rem] rounded-full bg-[#F28B64]/20 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40 blur-2xl" />
      </div>

      <main id="main-content" tabIndex={-1} className="relative z-10 w-full max-w-lg outline-none">
        {/* Brand strip */}
        <div className="mb-8 text-center">
          <p className="font-quicksand text-sm font-semibold uppercase tracking-[0.2em] text-brandNavy/70">MySipur</p>
          <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-gradient-to-r from-brandOrange to-brandBlue" />
        </div>

        <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/90 shadow-[0_25px_50px_-12px_rgba(64,89,107,0.18)] backdrop-blur-xl">
          {/* Top accent */}
          <div className="h-1.5 bg-gradient-to-r from-brandNavy via-brandOrange to-brandBlue" />

          <div className="px-8 pb-10 pt-9 sm:px-12 sm:pb-12 sm:pt-11">
            {status === "loading" && (
              <div className="text-center">
                <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-brandNavy/10 to-brandBlue/20 shadow-inner">
                  <IconMail />
                </div>
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center">
                  <LogoLoader size="sm" />
                </div>
                <h1 className="font-quicksand text-2xl font-bold tracking-tight text-heading sm:text-3xl">
                  Confirming your email
                </h1>
                <p className="mt-3 text-base leading-relaxed text-body">
                  We&apos;re securely verifying your link. This only takes a moment.
                </p>
                <p className="mt-6 text-xs text-gray-400">Please keep this tab open</p>
              </div>
            )}

            {status === "ok" && (
              <div className="text-center">
                <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 shadow-lg shadow-emerald-500/30">
                  <IconCheck />
                </div>
                <h1 className="font-quicksand text-2xl font-bold tracking-tight text-heading sm:text-3xl">
                  You&apos;re all set
                </h1>
                <p className="mt-3 text-base leading-relaxed text-body">{message}</p>
                <p className="mt-2 text-sm text-gray-500">Sign in anytime with your email and password.</p>
                <Link
                  to="/login"
                  className="mt-10 inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-brandOrange to-[#e07a52] px-6 py-3.5 font-semibold text-white shadow-lg shadow-orange-500/25 transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
                >
                  Continue to sign in
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="text-center">
                <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-2xl bg-rose-50 ring-1 ring-rose-100">
                  <IconAlert />
                </div>
                <h1 className="font-quicksand text-2xl font-bold tracking-tight text-heading sm:text-3xl">
                  We couldn&apos;t verify this link
                </h1>
                <p className="mt-4 rounded-2xl bg-rose-50/80 px-4 py-3 text-left text-sm leading-relaxed text-rose-900/90">
                  {message}
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  If your link expired, create a new account or contact support for help.
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex w-full items-center justify-center rounded-2xl bg-brandNavy px-6 py-3.5 font-semibold text-white shadow-lg shadow-brandNavy/20 transition hover:bg-[#354a5c] focus:outline-none focus:ring-2 focus:ring-brandNavy focus:ring-offset-2"
                >
                  Back to sign in
                </Link>
                <Link
                  to="/signup"
                  className="mt-3 block text-center text-sm font-semibold text-brandNavy underline-offset-2 hover:underline"
                >
                  Create a new account
                </Link>
              </div>
            )}
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-gray-400">
          Questions? We&apos;re here to help you get started with your storybook.
        </p>
      </main>
      <div className="relative z-10 mt-auto w-full max-w-lg px-4">
        <SiteFooter />
      </div>
    </div>
  );
};

export default VerifyEmail;
